import * as React from 'react';
import './PaymentsSettings.scss'
import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { Button, Modal, ModalHeader, ModalBody, Form, ButtonGroup, Table } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import ButtonsContainer from '../../../components/ButtonsContainer';
import { NumberInputField, SelectTextField, TextInputField } from '../../../components/Forms/fields';
import { Row } from '../../../components/Forms/table/Row';
import { Col } from '../../../components/Forms/table/Column';
import { PaymentProfile, PaymentProfileType, ProfessionalPayment, ProfessionalPaymentRow, ProfessionalPaymentType } from '../../../models/api-models';
import ToolTip from '../../../components/ToolTip';
import { ProfessionalPaymentSingleInput } from '../../../components/Forms/ProfessionalForm/ProfessionalPaymentSingle';

export interface PaymentConfigurationModalProps {
    defaultValue?: PaymentProfile,
    onClose?: () => void,
    onSave: (value: PaymentProfile) => void,
    onDelete: (id: string) => void,
}

const PaymentConfigurationModal : FC<PaymentConfigurationModalProps> = ({
    defaultValue,
    onClose,
    onSave,
    onDelete,
}) => {

    const t = useTranslations();
    
    const [profile, setProfile] = useState<PaymentProfile>({id: '', name: '', creationDate: '', table: [], version: 0, type: 0, ...defaultValue})
    const [rowType, setRowType] = useState<'nrHours'|'nrAppointments'>('nrAppointments')

    const rowTypesMap = useMemo<Record<typeof rowType, string>>(() => ({
        nrHours: t(TK.nrHours),
        nrAppointments: t(TK.nrAppointments),
    }), [t])

    const rowTypesMapBack = useMemo<Record<string, typeof rowType>>(
        () => Object.fromEntries(Object.entries(rowTypesMap).map(([k,v])=>[v,k as typeof rowType])), 
        [rowTypesMap])

    useEffect(() => {
        if(!defaultValue) return
        setRowType(defaultValue.table[0]?.nrHours > 0 ? 'nrHours' : 'nrAppointments')
        setProfile(defaultValue)
    }, [defaultValue]);


    const updateName = useCallback((name: string) => setProfile(prev => ({...prev, name})), [setProfile])
        
    const updateNrAppointments = useCallback((idx: number) => (nrAppointments?: number) => {
        setProfile(prev => ({
            ...prev,
            table: idx >= prev.table.length
                ? [...prev.table, {nrAppointments} as ProfessionalPaymentRow] 
                : nrAppointments
                    ? prev.table.map((v, i) => i !== idx ? v : {...v, nrAppointments})
                    : prev.table.filter((_, i) => i !== idx),
        }))
    }, [setProfile])

    const updateNrHours = useCallback((idx: number) => (nrHours?: number) => {
        setProfile(prev => ({
            ...prev,
            table: idx >= prev.table.length
                ? [...prev.table, {nrHours} as ProfessionalPaymentRow] 
                : nrHours
                    ? prev.table.map((v, i) => i !== idx ? v : {...v, nrHours})
                    : prev.table.filter((_, i) => i !== idx),
        }))
    }, [setProfile])
   
    const updatePayment = useCallback((idx: number) => (payment: ProfessionalPayment) => {
        setProfile(prev => ({
            ...prev,
            table: prev.table.map((v, i) => i !== idx ? v : {...v, ...payment}),
        }))
    }, [setProfile])

    const resetTable = useCallback(() => {
        setProfile(prev => ({...prev, table: []}))
    }, [setProfile])

    const tableOrder = useMemo(
        () => profile.table.map(r => profile.table.filter(v => r.nrAppointments ? v.nrAppointments < r.nrAppointments : v.nrHours < r.nrHours).length), 
        [profile.table],
    )

    const typeAbsoluteClick = useCallback(() => setProfile(prev => ({...prev, type: PaymentProfileType.Absolute})), [setProfile])
    const typeStepsClick = useCallback(() => setProfile(prev => ({...prev, type: PaymentProfileType.Steps})), [setProfile])

    const id = useMemo(() => `payment-profile-${defaultValue?.id ?? 'new'}`, [defaultValue])

    return (
        <Modal size="lg" isOpen={!!defaultValue}>
            <ModalHeader>{t(TK.paymentProfileConfiguration)} - {profile?.name || t(TK.new)} (v{profile?.version || '1'})</ModalHeader>
            <ModalBody>
                { profile &&
                    <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
                        <Row>
                            <Col md={12}>
                                <TextInputField
                                    id={`${id}-name`}
                                    label={t(TK.name)}
                                    value={profile.name}
                                    onChange={updateName}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <SelectTextField
                                    label={t(TK.by)}
                                    value={rowTypesMap[rowType]}
                                    onValueChanged={v => {setRowType(rowTypesMapBack[v]); resetTable()}}
                                    options={Object.values(rowTypesMap)}
                                    allowUndefined={false}
                                />
                            </Col>
                            <Col md={8}>
                                <ButtonGroup>
                                    <Button id='type-absolute-button' onClick={typeAbsoluteClick} active={profile.type === PaymentProfileType.Absolute}>{t(TK.absoluteValues)}</Button>
                                    <Button id='type-steps-button' onClick={typeStepsClick} active={profile.type === PaymentProfileType.Steps}>{t(TK.bySteps)}</Button>

                                    <ToolTip target='type-absolute-button' toolTipContent={t(TK.absoluteValuesPaymentProfileDesc)}/>
                                    <ToolTip target='type-steps-button' toolTipContent={t(TK.stepsPaymentProfileDesc)}/>
                                </ButtonGroup>
                            </Col>
                        </Row>
                        <hr style={{width: '100%'}}/>
                        <h3>{t(TK.table)}</h3>
                        <div className='PaymentTable'>
                            <div>{t(TK[rowType])}</div>
                            {profile.table.concat([{} as any]).map((row, idx) => (
                                <Row key={`${idx}`} style={{order: tableOrder[idx] ?? 1000}}>
                                    { rowType === 'nrHours'
                                        ? <Col md={3} lg={2}>
                                            <NumberInputField 
                                                id={`${id}-table-row-${idx}-nr-hours`}
                                                value={row.nrHours} 
                                                onChange={updateNrHours(idx)}/>
                                        </Col>
                                        : <Col md={3} lg={2}>
                                            <NumberInputField 
                                                id={`${id}-table-row-${idx}-nr-appointments`}
                                                value={row.nrAppointments} 
                                                onChange={updateNrAppointments(idx)}/>
                                        </Col>
                                    }
                                    <Col md={9} lg={6}>
                                        <ProfessionalPaymentSingleInput
                                            id={`${id}-table-row-${idx}-payment`}
                                            data={row}
                                            types={[
                                                ProfessionalPaymentType.Fixed,
                                                ProfessionalPaymentType.PerHour,
                                                ProfessionalPaymentType.Percentage,
                                            ]}
                                            onChange={updatePayment(idx)}/>
                                    </Col>
                                </Row>
                            ))}
                        </div>
                    </Form>
                }

                <hr/>

                <ButtonsContainer>
                    <Button id={`${id}-delete-button`} color="danger" onClick={() => onDelete(profile.id)}>{t(TK.delete)}</Button>
                    <Button id={`${id}-save-button`} disabled={!profile.name?.length} color="success" onClick={() => onSave(profile)}>{t(TK.save)}</Button>
                    <Button id={`${id}-cancel-button`} onClick={onClose}>{t(TK.cancel)}</Button>
                </ButtonsContainer>
            </ModalBody>
        </Modal>
    );
}

export default PaymentConfigurationModal;